import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiAward } from 'react-icons/fi';

const TABS = [
  { key: 'by_country',  label: 'Country' },
  { key: 'by_customer', label: 'Customer' },
  { key: 'by_incharge', label: 'Sales Incharge' }
];

const COLORS = ['#6366F1','#3B82F6','#10B981','#F59E0B','#EF4444','#A855F7','#EC4899','#06B6D4','#F97316','#84CC16'];

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

const RankList = ({ items }) => {
  const maxVal = Math.max(...(items || []).map(i => i.total_value), 1);
  if (!items || items.length === 0) return (
    <p className="tw-text-gray-400 tw-text-sm tw-text-center tw-py-8">No data available</p>
  );

  return (
    <div className="tw-space-y-2.5">
      {items.map((item, i) => {
        const color = COLORS[i % COLORS.length];
        const pct = (item.total_value / maxVal) * 100;
        return (
          <div key={i} className="tw-flex tw-items-center tw-gap-3">
            {/* Rank badge */}
            <div
              className="tw-w-6 tw-h-6 tw-rounded-xl tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-text-white tw-text-xs tw-font-bold"
              style={{ backgroundColor: i < 3 ? color : '#D1D5DB' }}
            >
              {i + 1}
            </div>
            <div className="tw-flex-1 tw-min-w-0">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                <span className="tw-text-xs tw-font-semibold tw-text-gray-800 tw-truncate tw-pr-2">{item.name || '—'}</span>
                <div className="tw-flex-shrink-0 tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap tw-justify-end">
                  <span className="tw-text-xs tw-font-bold tw-text-gray-900">{fmtM(item.total_value)}</span>
                  <span className="tw-text-xs tw-text-gray-400">· {item.project_count} proj</span>
                  {item.total_weight > 0 && (
                    <span className="tw-text-xs tw-text-cyan-600 tw-font-medium">· {Number(item.total_weight).toLocaleString('en-US', { maximumFractionDigits: 0 })} MT</span>
                  )}
                </div>
              </div>
              <div className="tw-h-1.5 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden">
                <div className="tw-h-full tw-rounded-xl tw-transition-all tw-duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
RankList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
};

const TopContractsChart = ({ data, loading, error }) => {
  const [activeTab, setActiveTab] = useState('by_country');

  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading top contracts</p>
    </Card>
  );

  const items = (data || {})[activeTab] || [];

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-yellow-50 tw-via-white tw-to-orange-50 tw-shadow-sm">
      <div className="tw-p-5 tw-border-b tw-border-gray-200">
        <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiAward className="tw-text-yellow-500" size={18} />
          Top Contracts
        </h3>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">Top 10 by total pipeline value</p>
      </div>

      {/* Tab bar */}
      <div className="tw-flex tw-border-b tw-border-gray-100">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`tw-flex-1 tw-py-2.5 tw-text-xs tw-font-semibold tw-transition-colors tw-cursor-pointer tw-border-b-2 ${
              activeTab === tab.key
                ? 'tw-border-blue-600 tw-text-blue-600 tw-bg-blue-50'
                : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tw-p-5">
        <RankList items={items} />
      </div>
    </Card>
  );
};

TopContractsChart.propTypes = { data: PropTypes.object, loading: PropTypes.bool, error: PropTypes.any };
TopContractsChart.defaultProps = { data: null, loading: false, error: null };

export default TopContractsChart;
